/**
 * @file dialog-mobile.js
 * @description Componente drawer mobile para dialogs (CRA)
 */

import React from "react"
import { X } from "lucide-react"
import { Button } from "../../../ui/button.js"
import { cn } from "../../../../lib/utils.js"
import { getDialogColors, getDialogIcon } from "../utils/dialog-utils.js"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "../../../ui/drawer.js"
import { useIsMobile } from "../../../../hooks/use-mobile.js"

/**
 * Componente drawer mobile para dialogs
 * @param {Object} props - Propriedades do componente
 * @returns {React.ReactElement} Componente drawer mobile
 */
export function DialogMobile({
  open,
  onClose,
  title = "Informação",
  message = "",
  type = "info",
  confirmText = "OK",
  cancelText = "Cancelar",
  onConfirm,
  onCancel,
  showCancel = false,
  className,
}) {
  const colors = getDialogColors(type)
  const icon = getDialogIcon(type)
  const isMobile = useIsMobile()

  const handleConfirm = () => {
    onClose()
    if (onConfirm) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    onClose()
    if (onCancel) {
      onCancel()
    }
  }

  if (!isMobile || !open) return null

  const handleDrawerState = (value) => {
    if (!value) {
      onClose()
    }
  }

  return React.createElement(
    Drawer,
    {
      open,
      onOpenChange: handleDrawerState,
    },
    React.createElement(
      DrawerContent,
      {
        className: cn(
          "max-w-lg mx-auto rounded-t-[28px] bg-white shadow-[0_-12px_40px_rgba(15,23,42,0.18)]",
          className
        ),
      },
      React.createElement(
        DrawerHeader,
        {
          className:
            "flex flex-col gap-2 px-6 pt-4 pb-3 text-center relative border-b border-gray-100",
        },
        React.createElement("span", {
          className:
            "absolute top-1 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-gray-300",
        }),
        React.createElement(
          "div",
          { className: "flex justify-center mb-2 text-xl" },
          icon
        ),
        React.createElement(
          DrawerTitle,
          { className: "text-base font-semibold text-gray-900" },
          title
        ),
        message &&
          React.createElement(
            DrawerDescription,
            { className: "text-sm text-gray-500" },
            message
          ),
        React.createElement(
          DrawerClose,
          { className: "absolute right-4 top-4" },
          React.createElement(X, { className: "w-5 h-5 text-gray-400" })
        )
      ),
      React.createElement(
        "div",
        { className: "flex-1 overflow-y-auto px-4 pb-6 pt-4" },
        React.createElement(
          DrawerFooter,
          { className: "flex flex-col gap-3" },
          showCancel &&
            React.createElement(
              Button,
              {
                variant: "outline",
                onClick: handleCancel,
                className: "w-full",
              },
              cancelText
            ),
          React.createElement(
            Button,
            {
              onClick: handleConfirm,
              className: cn("w-full", colors.confirmButton),
            },
            confirmText
          )
        )
      )
    )
  )
}

export default DialogMobile
